import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { IndexComponent } from './index/index.component';
import { CoursesComponent } from './courses/courses.component';
import { CourseDetailsComponent } from './courses/course-details/course-details.component';
import { CourseEnrollComponent } from './courses/course-enroll/course-enroll.component';
import { AddCourseComponent } from './courses/add-course/add-course.component';
import { CourseIndexComponent } from './courses/course-index/course-index.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { PlaygroundComponent } from './shared/component/playground/playground.component';
import { PlaylistComponent } from './courses/playlist/playlist.component';
import { LoginComponent } from './auth/login/login.component';
import { SignupComponent } from './auth/signup/signup.component';
import { ParentalConsentComponent } from './auth/parental-consent/parental-consent.component';
import { PendingConsentComponent } from './auth/pending-consent/pending-consent.component';
import {
  canActivateProtected,
  canActivateRoles,
  canActivateGuest,
} from './shared/util/routes.guard';
import { AddCourseSuccessComponent } from './courses/add-course-success/add-course-success.component';
import { VideoComponent } from './courses/playlist/video/video.component';
import { VerifyUserComponent } from './auth/verify-user/verify-user.component';
import { UserRoles } from './shared/core/state/users/models';
import { OrderPaymentComponent } from './order/payment/order-payment.component';
import { PaymentSuccessComponent } from './order/payment/payment-success/payment-success.component';
import { OrderComponent } from './order/order.component';
import { MyCoursesComponent } from './courses/my-courses/my-courses.component';
import { InstructorComponent } from './instructor/instructor.component';
import { LiveClassesComponent } from './live-classes/live-classes.component';
import { AddLiveClassComponent } from './live-classes/add-live-class/add-live-class.component';
import { LiveClassDetailsComponent } from './live-classes/live-class-details/live-class-details.component';
import { SubscriptionComponent } from './subscription/subscription.component';
import { AddSubscriptionComponent } from './live-classes/add-subscription/add-subscription.component';
import { InvoicePaymentComponent } from './subscription/invoice-payment/invoice-payment.component';
import { InvoicePaymentSuccessComponent } from './subscription/invoice-payment-success/invoice-payment-success.component';
import { LearningSpaceComponent } from './learning-space/learning-space.component';
import { PasswordResetComponent } from './auth/password-reset/password-reset.component';
import { AddUserProfileComponent } from './user-profile/add-user-profile/add-user-profile.component';
import { AddLiveClassSuccessComponent } from './live-classes/add-live-class-success/add-live-class-success.component';
import { ZoomOauthCallbackComponent } from './auth/zoom-oauth-callback/zoom-oauth-callback.component';
import { PastMeetingsComponent } from './past-meetings/past-meetings.component';
import { LiveClassContentsComponent } from './learning-space/live-class-contents/live-class-contents.component';
import { InvoicesComponent } from './invoices/invoices.component';
import { InvoicePreviewComponent } from './invoices/invoice-preview/invoice-preview.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { TermsComponent } from './terms/terms.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { RefundComponent } from './refund/refund.component';
import { ZoomDocumentationComponent } from './zoom-documentation/zoom-documentation.component';
import { ContactComponent } from './contact/contact.component';
import { DashboardOverviewComponent } from './dashboard/dashboard-overview/dashboard-overview.component';
import { UserProfileComponent } from './user-profile/user-profile.component';
import { EarningsComponent } from './dashboard/earnings/earnings.component';
import { AppManagementComponent } from './dashboard/app-management/app-management.component';
import { CourseManagementComponent } from './dashboard/course-management/course-management.component';
import { AcademySettingsComponent } from './dashboard/academy-settings/academy-settings.component';
import { StudentManagementComponent } from './dashboard/student-management/student-management.component';
import { BatchManagementComponent } from './dashboard/batch-management/batch-management.component';
import { MyBatchesComponent } from './dashboard/my-batches/my-batches.component';
import { PaymentManagementComponent } from './dashboard/payment-management/payment-management.component';
import { WorkspaceUsageComponent } from './dashboard/workspace-usage/workspace-usage.component';
import { TutorsDirectoryComponent } from './tutors-directory/tutors-directory.component';
import { TutorProfileViewComponent } from './tutor-profile-view/tutor-profile-view.component';
import { TutorProfileEditorComponent } from './user-profile/tutor-profile-editor/tutor-profile-editor.component';
import { TutorProfilePageComponent } from './user-profile/tutor-profile-page/tutor-profile-page.component';
import { TeachComponent } from './teach/teach.component';
import { ExploreLiveClassesComponent } from './dashboard/explore-live-classes/explore-live-classes.component';
import {
  canActivatePlatformOnly,
  appModeLandingGuard,
} from './shared/util/platform-only.guard';

const routes: Routes = [
  { path: '', component: IndexComponent, canActivate: [appModeLandingGuard] },
  {
    path: 'teach',
    component: TeachComponent,
    canActivate: [canActivatePlatformOnly],
  },
  { path: 'become-a-tutor', redirectTo: 'teach', pathMatch: 'full' },
  {
    path: 'contact',
    component: ContactComponent,
    canActivate: [canActivatePlatformOnly],
  },
  {
    path: 'tutors',
    component: TutorsDirectoryComponent,
    canActivate: [canActivatePlatformOnly],
  },
  {
    path: 'tutors/:identifier',
    component: TutorProfileViewComponent,
    canActivate: [canActivatePlatformOnly],
  },

  // auth
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [canActivateGuest],
      },
      {
        path: 'register',
        component: SignupComponent,
        canActivate: [canActivateGuest],
      },
      {
        path: 'verify-user',
        component: VerifyUserComponent,
      },
      {
        path: 'parent-consent',
        component: ParentalConsentComponent,
      },
      {
        path: 'pending-consent',
        component: PendingConsentComponent,
      },
      {
        path: 'reset-password',
        component: PasswordResetComponent,
      },
      {
        path: 'zoom-callback',
        component: ZoomOauthCallbackComponent,
      },
    ],
  },

  // user
  {
    path: 'user',
    children: [{ path: 'update-profile', component: AddUserProfileComponent }],
  },

  // courses
  {
    path: 'courses',
    component: CoursesComponent,
    children: [
      {
        path: '',
        component: CourseIndexComponent,
      },
      {
        path: 'add',
        component: AddCourseComponent,
        canActivate: [canActivateProtected, canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'add-success/:id',
        component: AddCourseSuccessComponent,
        canActivate: [canActivateProtected],
      },
      {
        path: 'edit',
        component: AddCourseComponent,
        canActivate: [canActivateProtected, canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
          editMode: true,
        },
      },
      {
        path: 'edit/:id',
        component: AddCourseComponent,
        canActivate: [canActivateProtected, canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
          editMode: true,
        },
      },
      {
        path: ':id/playlist',
        component: PlaylistComponent,
        children: [
          {
            path: 'video/:id',
            component: VideoComponent,
          },
        ],
      },
      {
        path: 'my-courses',
        redirectTo: '/dashboard/my-courses',
        pathMatch: 'full',
      },
      {
        path: 'preview/:id',
        component: CourseDetailsComponent,
      },
      {
        path: ':id/enroll',
        component: CourseEnrollComponent,
        canActivate: [canActivateProtected],
      },
      {
        path: ':id',
        component: CourseDetailsComponent,
      },
    ],
  },

  // live-class alias
  {
    path: 'live-class',
    redirectTo: 'live-classes',
    pathMatch: 'full',
  },

  // live-classes
  {
    path: 'live-classes',
    children: [
      {
        path: '',
        component: LiveClassesComponent,
      },
      {
        path: 'add',
        canActivate: [canActivateProtected, canActivateRoles],
        component: AddLiveClassComponent,
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'add-success/:id',
        component: AddLiveClassSuccessComponent,
        canActivate: [canActivateProtected],
      },
      {
        path: ':id/subscribe',
        canActivate: [canActivateProtected],
        component: AddSubscriptionComponent,
      },
      {
        path: ':id',
        component: LiveClassDetailsComponent,
      },
      {
        path: 'edit/:id',
        component: AddLiveClassComponent,
        canActivate: [canActivateProtected, canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
          editMode: true,
        },
      },
      {
        path: 'preview/:id',
        component: LiveClassDetailsComponent,
      },
    ],
  },

  // meetings - RealtimeKit / Dyte meeting route added for eager loading
  {
    path: 'meetings/join/rtk/:meetingId',
    loadComponent: () =>
      import('./meetings/third-party/dyte-meeting/dyte-meeting.component').then(
        (m) => m.DyteMeetingComponent,
      ),
    canActivate: [canActivateProtected],
  },
  {
    path: 'meetings/join/realtimekit/:meetingId',
    redirectTo: 'meetings/join/rtk/:meetingId',
  },
  {
    path: 'meetings/join/dyte/:meetingId',
    redirectTo: 'meetings/join/rtk/:meetingId',
  },

  // meetings - lazy loaded module for other meeting routes (join/feedback)
  {
    path: 'meetings',
    loadChildren: () =>
      import('./meetings/meetings.module').then((m) => m.MeetingsModule),
  },

  // past-meetings play (full-page recording player)
  {
    path: 'past-meetings/play/:sessionId',
    loadComponent: () =>
      import(
        './past-meetings/recording-player/recording-player.component'
      ).then((m) => m.RecordingPlayerComponent),
    canActivate: [canActivateProtected],
  },

  // Dedicated Full-Page Site Studio (outside dashboard shell)
  {
    path: 'site-editor',
    loadComponent: () =>
      import(
        './dashboard/landing-page-editor/landing-page-editor.component'
      ).then((m) => m.LandingPageEditorComponent),
    canActivate: [canActivateProtected, canActivateRoles],
    data: {
      canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
    },
  },
  {
    path: 'landing-page-editor',
    redirectTo: 'site-editor',
    pathMatch: 'full',
  },

  // Backward-compat redirects for old standalone URLs
  {
    path: 'my-learnings',
    redirectTo: '/dashboard/learning',
    pathMatch: 'full',
  },
  {
    path: 'my-learnings/live-class/:id',
    redirectTo: ({ params }) =>
      `/dashboard/learning/live-class/${params['id']}`,
  },
  {
    path: 'past-meetings',
    redirectTo: '/dashboard/past-recordings',
    pathMatch: 'full',
  },
  {
    path: 'instructor',
    redirectTo: '/dashboard/instructor',
    pathMatch: 'full',
  },

  // Unified dashboard with sidebar
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [canActivateProtected],
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      // Backward compat: old /dashboard/user URL
      { path: 'user', redirectTo: 'overview', pathMatch: 'full' },
      { path: 'overview', component: DashboardOverviewComponent },
      { path: 'explore-classes', component: ExploreLiveClassesComponent },
      {
        path: 'live-classes',
        redirectTo: 'explore-classes',
        pathMatch: 'full',
      },
      { path: 'learning', component: LearningSpaceComponent },
      {
        path: 'learning/live-class/:id',
        component: LiveClassContentsComponent,
      },
      { path: 'my-courses', component: MyCoursesComponent },
      { path: 'my-batches', component: MyBatchesComponent },
      {
        path: 'meetings',
        loadChildren: () =>
          import('./meetings/meetings.module').then((m) => m.MeetingsModule),
      },
      { path: 'past-recordings', component: PastMeetingsComponent },
      { path: 'orders', component: OrderComponent },
      { path: 'order', redirectTo: 'orders', pathMatch: 'full' },
      { path: 'subscriptions', component: SubscriptionComponent },
      { path: 'invoices', component: InvoicesComponent },
      { path: 'profile/invoices', redirectTo: 'invoices', pathMatch: 'full' },
      { path: 'profile', component: UserProfileComponent },
      {
        path: 'profile/tutor-profile',
        component: TutorProfilePageComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'profile/tutor-settings',
        component: TutorProfileEditorComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'earnings',
        component: EarningsComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'instructor',
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
        children: [
          { path: '', component: InstructorComponent },
          { path: 'live-class/:id', component: InstructorComponent },
        ],
      },
      {
        path: 'courses',
        component: CourseManagementComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'app-management',
        component: AppManagementComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'students',
        component: StudentManagementComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'batches',
        component: BatchManagementComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'payments',
        component: PaymentManagementComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'academy-settings',
        component: AcademySettingsComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'workspace-usage',
        component: WorkspaceUsageComponent,
        canActivate: [canActivateRoles],
        data: {
          canAccessList: [UserRoles.ADMIN, UserRoles.INSTRUCTOR],
        },
      },
      {
        path: 'site-editor',
        redirectTo: '/site-editor',
        pathMatch: 'full',
      },
      {
        path: 'landing-page-editor',
        redirectTo: '/site-editor',
        pathMatch: 'full',
      },
    ],
  },

  // order
  {
    path: 'order',
    canActivate: [canActivateProtected],
    children: [
      {
        path: '',
        redirectTo: '/dashboard/orders',
        pathMatch: 'full',
      },
      {
        path: 'cart',
        redirectTo: '/courses',
        pathMatch: 'full',
      },
      {
        path: 'payment',
        component: OrderPaymentComponent,
      },
      {
        path: 'payment-success',
        component: PaymentSuccessComponent,
      },
    ],
  },
  {
    path: 'orders',
    redirectTo: '/dashboard/orders',
    pathMatch: 'full',
  },

  // subscription
  {
    path: 'subscription',
    canActivate: [canActivateProtected],
    children: [
      {
        path: '',
        component: SubscriptionComponent,
      },
      {
        path: 'invoice/:id/pay',
        component: InvoicePaymentComponent,
      },
      {
        path: 'payment-success',
        component: InvoicePaymentSuccessComponent,
      },
    ],
  },

  // invoices
  {
    path: 'invoices',
    canActivate: [canActivateProtected],
    children: [
      {
        path: '',
        component: InvoicesComponent,
      },
      {
        path: ':id/preview',
        component: InvoicePreviewComponent,
      },
    ],
  },
  {
    path: 'profile/invoices',
    redirectTo: 'invoices',
    pathMatch: 'full',
  },

  // admin
  {
    path: 'admin',
    loadChildren: () =>
      import('./admin-dashboard/admin-dashboard.module').then(
        (m) => m.AdminDashboardModule,
      ),
    canActivate: [canActivateRoles],
  },

  // TODO: Remove this after development
  {
    path: 'playground',
    component: PlaygroundComponent,
  },

  { path: 'terms', component: TermsComponent },
  { path: 'privacy', component: PrivacyComponent },
  { path: 'refund', component: RefundComponent },
  { path: 'zoom-documentation', component: ZoomDocumentationComponent },
  { path: 'zoom-docs', redirectTo: 'zoom-documentation', pathMatch: 'full' },
  { path: '404', component: NotFoundComponent },
  { path: '**', redirectTo: '/404' },
];

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
